// frontend/src/services/api.js - Complete file
import axios from 'axios';

// Determine if we're in development or production mode
const isDevelopment = process.env.NODE_ENV === 'development';

// API URLs configuration
const API_CONFIG = {
  // Production URLs
  production: {
    API_URL: window.location.origin + '/api',
    SF_PROXY_URL: window.location.origin + '/sf'
  },
  // Development URLs - use these when running locally
  development: {
    API_URL: 'http://localhost:5000/api',
    SF_PROXY_URL: 'http://localhost:5000/sf'
  }
};

// Select the appropriate configuration based on environment
const config = isDevelopment ? API_CONFIG.development : API_CONFIG.production;

// Create axios instance
const api = axios.create({
  baseURL: config.API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// SpiderFoot API access via backend proxy to avoid CORS issues
const sfApi = axios.create({
  baseURL: config.SF_PROXY_URL,
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Dedicated client for auth refresh that has NO interceptors to avoid recursion
const refreshClient = axios.create({
  baseURL: config.API_URL,
  timeout: 15000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper function to get token
const getToken = () => localStorage.getItem('token');

// Compatibility only: sessions created before HttpOnly-cookie migration may
// have a refresh token here until their first successful refresh.
const getRefreshToken = () => localStorage.getItem('refreshToken');

// Helper function to check if access token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// Refresh coordination state to avoid multiple concurrent refreshes
let isRefreshing = false;
let refreshPromise = null;
const refreshSubscribers = [];
let refreshWindowStart = 0;
let refreshAttemptsInWindow = 0;

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken) => {
  while (refreshSubscribers.length) {
    const cb = refreshSubscribers.shift();
    try { cb(newToken); } catch {}
  }
};

// Utility: identify auth endpoints to avoid recursive refresh logic
const isAuthRefreshRequest = (cfg) => (cfg?.url || '').includes('/auth/refresh');
const isAuthLoginRequest = (cfg) => (cfg?.url || '').includes('/auth/login');

// Helper function to refresh access token (single-flight)
const refreshAccessToken = async () => {
  const legacyRefreshToken = getRefreshToken();
  const hasKnownSession = Boolean(
    legacyRefreshToken || localStorage.getItem('user') || localStorage.getItem('token')
  );
  if (!hasKnownSession) return null;

  try {
    // Sliding window rate-limit: max 3 refreshes per minute
    const now = Date.now();
    if (now - refreshWindowStart > 60000) {
      refreshWindowStart = now;
      refreshAttemptsInWindow = 0;
    }
    refreshAttemptsInWindow += 1;
    if (refreshAttemptsInWindow > 3) {
      console.warn('Too many token refresh attempts in 60s; forcing logout to prevent loops');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return null;
    }

    // Use the dedicated refresh client WITHOUT interceptors
    const response = await refreshClient.post(
      '/auth/refresh',
      legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {}
    );
    if (response.data.tokens) {
      const { accessToken } = response.data.tokens;
      localStorage.setItem('token', accessToken);
      // The server has now issued/renewed the HttpOnly cookie.
      localStorage.removeItem('refreshToken');
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return accessToken;
    }
  } catch (error) {
    // Only clear tokens + redirect when the refresh endpoint EXPLICITLY rejects
    // the refresh token (400/401/403). Transient failures — network error, request
    // timeout, nginx 502, backend 5xx — must NOT throw away a still-valid 30-day
    // refresh token, otherwise a brief blip force-logs-out the user (across every
    // open tab) and loses their work. Keep the tokens and let the caller retry.
    const status = error?.response?.status;
    const refreshRejected = status === 400 || status === 401 || status === 403;
    if (refreshRejected) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else {
      console.warn('Transient token refresh failure; keeping tokens for retry:', error?.message);
    }
    return null;
  }
  return null;
};

// Return a valid access token for consumers that must embed a fresh token at
// (re)connect time — notably EventSource, whose native reconnect reuses the
// URL (and its now-expired token) forever. Refreshes only when the current
// token is actually expired, so a transient reconnect does not hammer /auth/refresh.
export const getValidAccessToken = async () => {
  const token = getToken();
  if (token && !isTokenExpired(token)) return token;
  return await refreshAccessToken();
};

// Add token to requests with automatic refresh (single-flight)
api.interceptors.request.use(
  async (config) => {
    // Skip any token/refresh handling for the refresh or login endpoint
    if (isAuthRefreshRequest(config) || isAuthLoginRequest(config)) {
      return config;
    }
    let token = getToken();

    // Check if token exists and is expired
    if (token && isTokenExpired(token)) {
      console.log('Access token expired, attempting refresh...');
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken()
          .finally(() => { isRefreshing = false; });
      }
      try {
        token = await refreshPromise;
        if (token) onTokenRefreshed(token);
      } catch {
        // Swallow; downstream will handle 401
      }
    } else if (!token) {
      // If a refresh is already in-flight (e.g., parallel requests at startup), wait for it
      if (isRefreshing && refreshPromise) {
        try {
          token = await refreshPromise;
          if (token) onTokenRefreshed(token);
        } catch {}
      }
    }

    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for automatic token refresh on 401 errors (single-flight)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Never try to refresh on refresh/login endpoints
      if (isAuthRefreshRequest(originalRequest) || isAuthLoginRequest(originalRequest)) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      try {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken()
            .finally(() => { isRefreshing = false; });
        }

        const newToken = await refreshPromise;
        if (newToken) {
          originalRequest.headers['x-auth-token'] = newToken;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Error handling helper
const handleApiError = (error, defaultValue = null, throwError = true) => {
  console.error('API Error:', error);
  if (throwError) {
    throw error;
  }
  return defaultValue;
};

// Authentication services
export const authService = {
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.tokens) {
        // Refresh token is issued only as an HttpOnly cookie.
        localStorage.setItem('token', response.data.tokens.accessToken);
        localStorage.removeItem('refreshToken');
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } else if (response.data.token) {
        // Fallback for backward compatibility
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },
  
  logout: async () => {
    try {
      const legacyRefreshToken = getRefreshToken();
      await api.post('/auth/logout', legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {});
    } catch (error) {
      console.warn('Backend logout failed, clearing local storage anyway:', error);
    }

    // Clear all tokens and user data
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },
  
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/user');
      // Update local cache to keep avatar/username in sync
      if (response.data) {
        localStorage.setItem('user', JSON.stringify(response.data));
      }
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = getToken();
    const storedUser = localStorage.getItem('user');
    return !!(token || storedUser);
  },

  // Get stored user data
  getStoredUser: () => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  },

  // Initialize auth state on app load
  initializeAuth: async () => {
    const token = getToken();
    const hasStoredSession = Boolean(localStorage.getItem('user') || getRefreshToken());

    if ((!token || isTokenExpired(token)) && hasStoredSession) {
      console.log('Initializing auth from refresh session...');
      await refreshAccessToken();
    }

    return authService.isAuthenticated();
  },
  
  updatePassword: async (passwordData) => {
    try {
      const response = await api.put('/auth/password', passwordData);
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },
  
  updateEmail: async (emailData) => {
    try {
      const response = await api.put('/auth/email', emailData);
      
      // Update stored user data if successful
      if (response.data && response.data.email) {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (userData) {
          userData.email = response.data.email;
          localStorage.setItem('user', JSON.stringify(userData));
        }
      }
      
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  }
};

// Admin services
export const adminService = {
  getUsers: async () => {
    try {
      const response = await api.get('/admin/users');
      // Handle both old format (direct array) and new format (wrapped in data)
      const users = response.data.data || response.data;
      return Array.isArray(users) ? users : [];
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  },
  
  createUser: async (userData) => {
    try {
      const response = await api.post('/admin/users', userData);
      // Handle both old format and new format (wrapped in data)
      return response.data.data || response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },

  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      const result = response.data.data || response.data;
      // If admin changed username/email, update local cache if same user
      if (result && result.user) {
        const current = JSON.parse(localStorage.getItem('user'));
        if (current && current.id === result.user.id) {
          localStorage.setItem('user', JSON.stringify({
            ...current,
            username: result.user.username,
            email: result.user.email
          }));
        }
      }
      return result;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },
  
  resetUserPassword: async (userId, passwordData) => {
    try {
      const response = await api.put(`/admin/users/${userId}/reset-password`, passwordData);
      return response.data.data || response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },

  updateUserAvatar: async (userId, imageBase64) => {
    try {
      const response = await api.put(`/admin/users/${userId}/avatar`, { imageBase64 });
      const result = response.data.data || response.data;
      // Update local cache if same user
      if (result && result.avatarUrl) {
        const current = JSON.parse(localStorage.getItem('user'));
        if (current && current.id === userId) {
          localStorage.setItem('user', JSON.stringify({ ...current, avatarUrl: result.avatarUrl }));
        }
      }
      return result;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },
  
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data.data || response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },

  // Feature Access Management APIs
  getUserFeatures: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/features`);
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },

  updateUserFeatures: async (userId, featureAccess, reason) => {
    try {
      const response = await api.put(`/admin/users/${userId}/features`, {
        featureAccess,
        reason
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },

  toggleUserFeature: async (userId, feature, reason) => {
    try {
      const response = await api.post(`/admin/users/${userId}/features/${feature}/toggle`, {
        reason
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },

  getUserFeatureHistory: async (userId, page = 1, limit = 50) => {
    try {
      const response = await api.get(`/admin/users/${userId}/features/history?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },

  bulkUpdateUserFeatures: async (userIds, featureAccess, reason) => {
    try {
      const response = await api.post('/admin/users/features/bulk-update', {
        userIds,
        featureAccess,
        reason
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, null, true);
    }
  },
  
  getAllScans: async () => {
    try {
      const response = await api.get('/admin/scans');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching all scans:', error);
      try {
        // Try direct SpiderFoot API as fallback with POST request through proxy
        const formData = new URLSearchParams();
        const response = await fetch(`${config.SF_PROXY_URL}/scanlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          return data || [];
        }
        return [];
      } catch (sfError) {
        console.error('SpiderFoot API error:', sfError);
        return [];
      }
    }
  },
  
  getSystemStats: async () => {
    try {
      const response = await api.get('/admin/stats');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching system stats:', error);
      return {
        totalUsers: 0,
        totalScans: 0,
        activeScans: 0,
        completedScans: 0,
        failedScans: 0,
        topUsers: []
      };
    }
  },
  
  // Export all scan data (admin only)
  exportAllScans: async (format = 'json') => {
    try {
      const response = await api.get(`/admin/export/${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        // Create a download link for the CSV file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'all_scans.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        return { success: true, message: 'CSV downloaded successfully' };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error exporting all scans:', error);
      throw error;
    }
  },
  
  // Get SMTP settings
  getSmtpSettings: async () => {
    try {
      const response = await api.get('/admin/smtp');
      return response.data;
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
      throw error;
    }
  },
  
  // Update SMTP settings
  updateSmtpSettings: async (settings) => {
    try {
      const response = await api.post('/admin/smtp', {
        host: settings.host,
        port: parseInt(settings.port),
        secure: settings.secure,
        authType: settings.authType || 'login',
        user: settings.user,
        // Only include password if provided
        ...(settings.pass ? { pass: settings.pass } : {}),
        from: settings.from,
        // Include OAuth2 credentials if provided
        ...(settings.authType === 'oauth2' ? {
          clientId: settings.clientId,
          // Only include secrets if provided
          ...(settings.clientSecret ? { clientSecret: settings.clientSecret } : {}),
          ...(settings.refreshToken ? { refreshToken: settings.refreshToken } : {}),
          ...(settings.accessToken ? { accessToken: settings.accessToken } : {})
        } : {})
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating SMTP settings:', error);
      
      // Make sure we preserve the error structure
      if (error.response && error.response.data) {
        throw {
          ...error,
          message: error.response.data.message || error.message
        };
      }
      throw error;
    }
  },
  
  // Test SMTP settings
  testSmtpSettings: async (email, settingsId = 'current') => {
    try {
      const response = await api.post(`/admin/smtp/${settingsId}/test`, { email });
      return response.data;
    } catch (error) {
      console.error('Error testing SMTP settings:', error);

      // Make sure we preserve the error response structure for better UI feedback
      if (error.response && error.response.data) {
        throw {
          ...error,
          message: error.response.data.message || error.message,
          response: {
            ...error.response,
            data: {
              success: false,
              message: error.response.data.message,
              error: error.response.data.error || error.message
            }
          }
        };
      }

      throw error;
    }
  },

  // New Multi-SMTP System Methods
  getAllSmtpSettings: async () => {
    try {
      const response = await api.get('/admin/smtp');
      return response.data;
    } catch (error) {
      console.error('Error fetching all SMTP settings:', error);
      return handleApiError(error, null, true);
    }
  },

  getSmtpStatus: async () => {
    try {
      const response = await api.get('/admin/smtp/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching SMTP status:', error);
      return handleApiError(error, null, true);
    }
  },

  getSmtpSetting: async (id) => {
    try {
      const response = await api.get(`/admin/smtp/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SMTP setting:', error);
      return handleApiError(error, null, true);
    }
  },

  createOrUpdateSmtpSetting: async (data) => {
    try {
      let response;
      if (data.id) {
        response = await api.put(`/admin/smtp/${data.id}`, data);
      } else {
        response = await api.post('/admin/smtp', data);
      }
      return response.data;
    } catch (error) {
      console.error('Error creating/updating SMTP setting:', error);
      return handleApiError(error, null, true);
    }
  },

  deleteSmtpSetting: async (id) => {
    try {
      const response = await api.delete(`/admin/smtp/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting SMTP setting:', error);
      return handleApiError(error, null, true);
    }
  },

  testSmtpSetting: async (id, email) => {
    try {
      const response = await api.post(`/admin/smtp/${id}/test`, { email });
      return response.data;
    } catch (error) {
      console.error('Error testing SMTP setting:', error);
      return handleApiError(error, null, true);
    }
  },

  testAllSmtpSettings: async (email) => {
    try {
      const response = await api.post('/admin/smtp/test-all', { email });
      return response.data;
    } catch (error) {
      console.error('Error testing all SMTP settings:', error);
      return handleApiError(error, null, true);
    }
  },

  toggleSmtpSetting: async (id) => {
    try {
      const response = await api.post(`/admin/smtp/${id}/toggle`);
      return response.data;
    } catch (error) {
      console.error('Error toggling SMTP setting:', error);
      return handleApiError(error, null, true);
    }
  },

  getSmtpHealth: async () => {
    try {
      const response = await api.get('/admin/smtp/health');
      return response.data;
    } catch (error) {
      console.error('Error fetching SMTP health:', error);
      return handleApiError(error, null, true);
    }
  },

  getSmtpLogs: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/admin/smtp/logs?${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SMTP logs:', error);
      return handleApiError(error, { logs: [], total: 0 }, true);
    }
  },

  getNotificationSettings: async () => {
    try {
      const response = await api.get('/admin/notifications');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching admin notification settings:', error);
      return handleApiError(error, { success: false, message: 'Failed to load notification settings' });
    }
  },

  updateNotificationSettings: async (settings) => {
    try {
      const response = await api.put('/admin/notifications', settings);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating admin notification settings:', error);
      return handleApiError(error, { success: false, message: 'Failed to update notification settings' });
    }
  },

  testNotificationSettings: async () => {
    try {
      const response = await api.post('/admin/notifications/test');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error testing admin notification settings:', error);
      return handleApiError(error, { success: false, message: 'Failed to test notification settings' });
    }
  }
};

// Helper function to get the original scanId without timestamp suffix
const getOriginalScanId = (scanId) => {
  if (!scanId) return '';
  // Check if the scanId has a timestamp suffix (added for cache busting)
  const parts = scanId.split('-');
  if (parts.length > 1 && !isNaN(parseInt(parts[parts.length - 1]))) {
    // Return the scan ID without the timestamp suffix
    return parts.slice(0, -1).join('-');
  }
  return scanId;
};

// Helper function to determine risk level from SpiderFoot data
const determineRiskLevel = (eventData) => {
  // For SpiderFoot data in array format: [timestamp, data, module, type]
  if (Array.isArray(eventData)) {
    const type = eventData[3] || '';
    const data = eventData[1] || '';
    const module = eventData[2] || '';
    
    // Critical findings
    if (/VULNERABILITY|PASSWORD_EXPOSED|API_KEY|CREDENTIAL|XSS|SQL_INJECTION|RCE|COMMAND_INJECTION/i.test(type)) {
      return 'CRITICAL';
    }
    
    // High risk findings
    if (/WEAK|EXPIRED|INSECURE|AFFILIATE|OPEN_PORT|LEAK/i.test(type) || 
        /port.*open/i.test(data) || 
        /CVE/i.test(data)) {
      return 'HIGH';
    }
    
    // Medium risk findings
    if (/DNS|SUBDOMAIN|DOMAIN|INTERNET_NAME|TCP_PORT|IP_ADDRESS/i.test(type)) {
      return 'MEDIUM';
    }
    
    // Low risk findings
    if (/URL|WEBSERVER|HTTP_CODE/i.test(type)) {
      return 'LOW';
    }
    
    // Default to INFO
    return 'INFO';
  }
  
  // For object format (MongoDB/backend data)
  if (typeof eventData === 'object') {
    const type = eventData.type || eventData.eventType || '';
    const data = eventData.data || eventData.value || '';
    const module = eventData.module || eventData.source || '';
    
    // Critical findings
    if (/VULNERABILITY|PASSWORD_EXPOSED|API_KEY|CREDENTIAL|XSS|SQL_INJECTION|RCE|COMMAND_INJECTION/i.test(type)) {
      return 'CRITICAL';
    }
    
    // High risk findings
    if (/WEAK|EXPIRED|INSECURE|AFFILIATE|OPEN_PORT|LEAK/i.test(type) || 
        /port.*open/i.test(data) || 
        /CVE/i.test(data)) {
      return 'HIGH';
    }
    
    // Medium risk findings
    if (/DNS|SUBDOMAIN|DOMAIN|INTERNET_NAME|TCP_PORT|IP_ADDRESS/i.test(type)) {
      return 'MEDIUM';
    }
    
    // Low risk findings
    if (/URL|WEBSERVER|HTTP_CODE/i.test(type)) {
      return 'LOW';
    }
    
    // Default to INFO
    return 'INFO';
  }
  
  // Default fallback
  return 'MEDIUM';
};

// Enhanced scan services
export const scanService = {
  // Get original scanId without timestamp suffix
  getOriginalScanId,
  
  // Get API base URL for direct downloads
  getApiBaseUrl: () => {
    return config.API_URL;
  },
  
  // Get scans from our backend
  getUserScans: async () => {
    try {
      const response = await api.get('/scans');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching user scans:', error);
      
      // Try direct SpiderFoot API if our backend fails
      try {
        // Use POST request for SpiderFoot API through proxy
        const response = await fetch(`${config.SF_PROXY_URL}/scanlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          return data || [];
        }
        return [];
      } catch (sfError) {
        console.error('SpiderFoot API error:', sfError);
        return [];
      }
    }
  },
  
  // Get scan by ID - used by SecurityAnalysis.js
  getScanById: async (scanId) => {
    if (!scanId) {
      console.error('getScanById called without a scanId');
      throw new Error('Scan ID is required');
    }

    // Store the original scanId without timestamp suffix
    const originalScanId = scanId.split('-')[0];
    console.log(`[getScanById] Fetching scan with ID: ${scanId}, original ID: ${originalScanId}`);

    try {
      // Try to get the scan directly first
      const response = await api.get(`/scans/${scanId}`);
      console.log(`[getScanById] Successfully fetched scan data for ${scanId}`);
      return response.data;
    } catch (error) {
      console.warn(`[getScanById] Error fetching scan data for ${scanId}: ${error.message}`);
      
      if (error.response && error.response.status === 404) {
        console.log(`[getScanById] Scan not found. Attempting to sync scan ${originalScanId}`);
        
        try {
          // Try to sync the scan
          await api.post(`/scans/${originalScanId}/sync`);
          console.log(`[getScanById] Sync attempt completed for ${originalScanId}`);
          
          // Try fetching again after sync
          try {
            const retryResponse = await api.get(`/scans/${scanId}`);
            console.log(`[getScanById] Successfully fetched scan data after sync for ${scanId}`);
            return retryResponse.data;
          } catch (retryError) {
            console.warn(`[getScanById] Error fetching scan data after sync: ${retryError.message}`);
            
            // If that fails, try getting scan details through the scan service
            try {
              console.log(`[getScanById] Attempting to get scan details through scanService for ${scanId}`);
              const scanDetails = await scanService.getScanDetails(scanId);
              console.log(`[getScanById] Successfully fetched scan details for ${scanId}`);
              return scanDetails;
            } catch (scanServiceError) {
              console.error(`[getScanById] All attempts to fetch scan data failed: ${scanServiceError.message}`);
              throw scanServiceError;
            }
          }
        } catch (syncError) {
          console.error(`[getScanById] Error syncing scan ${originalScanId}: ${syncError.message}`);
          
          // Try to use scanService as a final attempt
          try {
            const scanDetails = await scanService.getScanDetails(scanId);
            console.log(`[getScanById] Successfully fetched scan details after sync failure for ${scanId}`);
            return scanDetails;
          } catch (finalError) {
            console.error(`[getScanById] All recovery attempts failed. Generating mock data for ${scanId}`);
            
            // Last resort: generate mock data
            return {
              id: scanId,
              name: `Scan ${scanId}`,
              status: 'COMPLETED',
              createdAt: new Date().toISOString(),
              completedAt: new Date().toISOString(),
              target: 'Unknown Target',
              findings: [],
              modules: [],
              eventTypes: [],
              results: {
                totalEvents: 0,
                findings: []
              }
            };
          }
        }
      } else {
        console.error(`[getScanById] Error fetching scan: ${error.message}`);
        throw error;
      }
    }
  },
  
  // Get all scans (admin only) - added to fix missing function error
  getAllScans: async () => {
    try {
      const response = await api.get('/admin/scans');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching all scans:', error);
      try {
        // Try direct SpiderFoot API as fallback with POST request through proxy
        const formData = new URLSearchParams();
        const response = await fetch(`${config.SF_PROXY_URL}/scanlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          return data || [];
        }
        return [];
      } catch (sfError) {
        console.error('SpiderFoot API error:', sfError);
        return [];
      }
    }
  },
  
  // Get available event types directly from SpiderFoot
  getEventTypes: async () => {
    try {
      // First try our backend
      const response = await api.get('/scans/eventtypes');
      const rawTypes = response.data?.eventTypes ?? response.data;
      if (Array.isArray(rawTypes)) return rawTypes;
      if (rawTypes && typeof rawTypes === 'object') {
        return Object.entries(rawTypes).map(([id, value]) => ({
          id,
          name: typeof value === 'string' ? value : (value?.name || id)
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching event types from backend:', error);
      
      // Read-only metadata fallback through the restricted proxy.
      try {
        const response = await fetch(`${config.SF_PROXY_URL}/eventtypes`, {
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          return data;
        }
        return [];
      } catch (sfError) {
        console.error('SpiderFoot API error (getEventTypes):', sfError);
        return [];
      }
    }
  },
  
  // Get available modules directly from SpiderFoot
  getModules: async () => {
    try {
      const response = await api.get('/scans/modules');
      const rawModules = response.data?.modules ?? response.data;
      if (Array.isArray(rawModules)) return rawModules;
      if (rawModules && typeof rawModules === 'object') return Object.keys(rawModules);
      return [];
    } catch (error) {
      console.error('Error fetching modules from backend:', error);
      
      // Read-only metadata fallback through the restricted proxy.
      try {
        const response = await fetch(`${config.SF_PROXY_URL}/modules`, {
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          return data;
        }
        return [];
      } catch (sfError) {
        console.error('SpiderFoot API error (getModules):', sfError);
        return [];
      }
    }
  },
  
  // Create a new scan
  createScan: async (scanData) => {
    // All scan creation must pass through the Maphra backend so authorization,
    // inventory limits, Mongo tracking, and upstream rollback stay atomic.
    console.log('Creating scan with backend API:', scanData);
    const response = await api.post('/scans', scanData);
    return response.data;
  },
  
  // Manually sync a scan
  syncScan: async (scanId) => {
    try {
      console.log(`Manually syncing scan ${scanId}`);
      const response = await api.post(`/scans/${scanId}/sync`);
      return response.data;
    } catch (error) {
      console.error('Backend API error (syncScan):', error);
      throw error;
    }
  },
  
  // Get scan details with fallback to direct SpiderFoot API
  getScanDetails: async (scanId) => {
    try {
      console.log('Getting scan details for:', scanId);
      console.log('API base URL:', config.API_URL);
      console.log('Full request URL:', `${config.API_URL}/scans/${scanId}`);
      console.log('Token available:', !!getToken());
      
      // First try our backend
      const response = await api.get(`/scans/${scanId}`);
      console.log('Backend response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Backend API error (getScanDetails):', error);
      console.error('Error response:', error.response);
      console.error('Error config:', error.config);
      
      // Fallback to direct SpiderFoot API with POST through proxy
      try {
        console.log('Falling back to direct SpiderFoot API for scan details');
        
        // Create form data for scan info
        const scanInfoFormData = new URLSearchParams();
        scanInfoFormData.append('id', scanId);
        scanInfoFormData.append('by', 'id');
        
        // First get scan info with POST request through proxy
        const scanInfoResponse = await fetch(`${config.SF_PROXY_URL}/scansummary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          },
          body: scanInfoFormData
        });
        
        // Create form data for scan types
        const scanTypesFormData = new URLSearchParams();
        scanTypesFormData.append('id', scanId);
        scanTypesFormData.append('by', 'id');
        
        // Get scan types with POST request through proxy
        const scanTypesResponse = await fetch(`${config.SF_PROXY_URL}/scantypesummary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          },
          body: scanTypesFormData
        });
        
        if (scanInfoResponse.ok && scanTypesResponse.ok) {
          const scanInfo = await scanInfoResponse.json();
          const scanTypes = await scanTypesResponse.json();
          
          console.log('Successfully retrieved data from SpiderFoot directly');
          console.log('SpiderFoot scan info response:', scanInfo);
          console.log('SpiderFoot scan types response:', scanTypes);
          return {
            scan: {
              scanId: scanId,
              name: Array.isArray(scanInfo) ? scanInfo[0] : 'Unknown Scan',
              target: Array.isArray(scanInfo) ? scanInfo[1] : 'Unknown Target',
              created: Array.isArray(scanInfo) ? scanInfo[2] : null,
              started: Array.isArray(scanInfo) ? scanInfo[3] : null,
              finished: Array.isArray(scanInfo) ? scanInfo[4] : null,
              status: Array.isArray(scanInfo) ? scanInfo[5] : 'UNKNOWN',
              progress: 100, // Assume completed
              riskSummary: Array.isArray(scanInfo) && scanInfo.length > 6 && scanInfo[6] ? 
                scanInfo[6] : { HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 }
            },
            // Format scanTypes to match backend API structure with summary.byType
            summary: {
              byType: Array.isArray(scanTypes) ? 
                scanTypes.map(type => {
                  // Convert types to objects with type and count properties
                  if (typeof type === 'object' && type.type && type.count) {
                    return type; // Already in correct format
                  } else if (Array.isArray(type) && type.length >= 2) {
                    return { type: type[0], count: type[1] };
                  } else {
                    return { type: String(type), count: 0 };
                  }
                }) : []
            },
            // Keep the original data for backward compatibility
            data: scanTypes
          };
        }
      } catch (sfError) {
        console.error('SpiderFoot API fallback also failed:', sfError);
      }
      
      // If both backend and fallback failed, throw the original error
      throw error;
    }
  },
  
  // Get scan results by type with direct SpiderFoot fallback
  getScanResultsByType: async (scanId, type, page = 1, limit = 100) => {
    try {
      console.log(`Getting results for scan ${scanId}, type ${type}, page ${page}, limit ${limit}`);
      
      // Skip if no scanId or type provided
      if (!scanId || !type) {
        console.log('Missing scanId or type, returning empty results');
        return { events: [], pagination: { total: 0, page: 1, limit, pages: 0 }, eventTypes: [] };
      }
      
      // Use original scanId for API calls (without timestamp suffix)
      const originalScanId = getOriginalScanId(scanId);
      console.log(`Using original scanId: ${originalScanId} for API call`);
      
      // Ensure page and limit are valid integers
      const pageNum = parseInt(page) || 1;
      const limitNum = parseInt(limit) || 100;
      
      // Try our backend first
      const response = await api.get(`/scans/${originalScanId}/results`, {
        params: { 
          type, 
          page: pageNum,
          limit: limitNum 
        }
      });
      console.log('Results from backend:', response.data?.events?.length || 0, 'items');
      console.log('Pagination info:', response.data?.pagination);
      
      // Add pagination info if not present
      if (response.data && !response.data.pagination && response.data.events) {
        response.data.pagination = {
          total: response.data.events.length,
          page: pageNum,
          limit: limitNum,
          pages: Math.ceil(response.data.events.length / limitNum)
        };
      }
      
      return response.data;
    } catch (error) {
      console.error('Backend API error (getScanResultsByType):', error);
      
      // Fallback to direct SpiderFoot API with POST through proxy
      try {
        console.log('Falling back to direct SpiderFoot API for results');
        
        // Validate event type
        if (!type || type === 'Unknown') {
          console.error('Invalid eventType provided to getScanResults');
          return { events: [], pagination: { total: 0, page: 1, limit, pages: 0 }, eventTypes: [] };
        }
        
        // Use original scanId for API calls (without timestamp suffix)
        const originalScanId = getOriginalScanId(scanId);
        const pageNum = parseInt(page) || 1;
        const limitNum = parseInt(limit) || 100;
        
        // Prepare form data for POST request
        const formData = new URLSearchParams();
        formData.append('id', originalScanId);
        formData.append('eventType', type);
        formData.append('offset', (pageNum - 1) * limitNum);
        formData.append('limit', limitNum);
        
        console.log(`SpiderFoot API params: id=${originalScanId}, type=${type}, offset=${(pageNum - 1) * limitNum}, limit=${limitNum}`);
        
        const response = await fetch(`${config.SF_PROXY_URL}/scaneventresults`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          },
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Format the data to match our API format
          const formattedData = {
            events: Array.isArray(data) ? data.map(item => ({
              id: item.id || item._id,
              type: item.type || type,
              module: item.module,
              data: item.data,
              sourceData: item.source_data,
              sourceModule: item.source_module,
              time: new Date(item.generated * 1000).toISOString(),
              risk: item.risk || 0
            })) : [],
            pagination: {
              total: Array.isArray(data) ? data.length : 0,
              page: pageNum,
              limit: limitNum,
              pages: Math.ceil((Array.isArray(data) ? data.length : 0) / limitNum)
            },
            eventTypes: [type]
          };
          
          console.log('Results from SpiderFoot API:', formattedData.events.length, 'items');
          console.log('Pagination info from SpiderFoot:', formattedData.pagination);
          
          return formattedData;
        } else {
          console.error('SpiderFoot API returned error status:', response.status);
          return { events: [], pagination: { total: 0, page: pageNum, limit: limitNum, pages: 0 }, eventTypes: [] };
        }
      } catch (sfError) {
        console.error('SpiderFoot API error:', sfError);
        return { events: [], pagination: { total: 0, page: parseInt(page) || 1, limit: parseInt(limit), pages: 0 }, eventTypes: [] };
      }
    }
  },
  
  // Get latest scan data for a specific scan and module type - optimized
  getLatestScanData: async (scanId, type, timestamp = null) => {
    try {
      console.log(`[API] Getting latest data for scan ${scanId}, type ${type}, since ${timestamp || 'beginning'}`);
      
      // Use original scanId for API calls
      const originalScanId = getOriginalScanId(scanId);
      
      // Build URL with optional timestamp parameter and size limit
      let url = `${config.API_URL}/scans/${originalScanId}/latest/${type}`;
      const params = new URLSearchParams();
      
      if (timestamp) {
        params.append('timestamp', timestamp);
      }
      
      // Limit the number of new results to prevent UI overload
      params.append('limit', '100');
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      // Add timeout for the request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      try {
        // Try our backend first
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': getToken() || ''
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Backend API returned status ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`[API] Got ${data.length} new results since ${timestamp || 'beginning'}`);
        return Array.isArray(data) ? data : [];
      } catch (fetchError) {
        clearTimeout(timeoutId);
        throw fetchError;
      }
    } catch (error) {
      console.error('Backend API error (getLatestScanData):', error);
      
      // Fallback to direct SpiderFoot API with POST through proxy
      try {
        console.log('Falling back to direct SpiderFoot API for latest data');
        
        // Create form data for POST request
        const formData = new URLSearchParams();
        formData.append('id', scanId);
        if (type && type !== 'all') {
          formData.append('eventType', type);
        }
        
        const response = await fetch(`${config.SF_PROXY_URL}/scaneventresults`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`SpiderFoot API returned status ${response.status}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          // Filter by timestamp if provided
          let filteredData = data;
          if (timestamp) {
            filteredData = data.filter(item => {
              if (Array.isArray(item) && item[0]) {
                const itemTimestamp = new Date(item[0]).getTime();
                const compareTimestamp = new Date(timestamp).getTime();
                return itemTimestamp > compareTimestamp;
              }
              return false;
            });
          }
          
          // Transform the data to match our expected format
          return filteredData.map(item => {
            if (Array.isArray(item)) {
              return {
                data: item[1] || '',
                module: item[3] || 'Unknown Module',
                time: item[0] ? new Date(item[0]).toISOString() : new Date().toISOString(),
                type: type,
                sourceData: item[2] || ''
              };
            }
            return item;
          });
        }
        return [];
      } catch (sfError) {
        console.error('SpiderFoot API fallback also failed:', sfError);
        return []; // Return empty array instead of throwing
      }
    }
  },
  
  // Get full scan summary with ALL data (enterprise-grade)
  getFullScanSummary: async (scanId, useCache = true) => {
    try {
      const originalScanId = scanService.getOriginalScanId ? 
        scanService.getOriginalScanId(scanId) : scanId.split('_')[0];
      
      console.log(`Fetching full summary for scan ${originalScanId} (cache: ${useCache})`);
      
      const url = `${config.API_URL}/scans/${originalScanId}/full-summary${useCache ? '' : '?cache=false'}`;
      const response = await api.get(url);
      
      if (response.data) {
        console.log(`✅ Full summary loaded: ${response.data.totalEvents} events, ${response.data.eventTypes?.length} types`);
        console.log(`Risk distribution:`, response.data.riskDistribution);
        return response.data;
      }
      
      throw new Error('Failed to get full summary');
    } catch (error) {
      console.error('Error fetching full scan summary:', error);
      throw error;
    }
  },
  
  // Export scan report
  exportScanReport: async (scanId, format = 'json', options = {}) => {
    try {
      const { useStreaming = true, page = null, limit = null } = options;
      
      console.log(`Exporting scan ${scanId} in ${format} format (streaming: ${useStreaming})`);
      
      // Get the token for authentication
      let token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Get the original scanId without timestamp suffix
      const originalScanId = scanService.getOriginalScanId ? 
        scanService.getOriginalScanId(scanId) : scanId.split('_')[0];
      
      // Build URL with query parameters
      let url = `${config.API_URL}/scans/${originalScanId}/export/${format}`;
      const params = new URLSearchParams();
      
      if (useStreaming && format === 'json') {
        params.append('stream', 'true');
      }
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log(`Export URL: ${url}`);
      
      try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': format === 'csv' ? 'text/csv' : 'application/json',
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
          console.error(`Export error from backend: ${response.status} ${response.statusText}`);
          
          // For aborted scans or other issues, try to generate a report manually
          if (format === 'json' && (response.status === 404 || response.status === 500)) {
            console.log('Attempting to generate report manually for potentially aborted scan');
            
            // Get event types
            const eventTypes = await scanService.getScanEventTypes(originalScanId);
            
            // Generate a basic scan info object
            const scanInfo = {
              id: scanId,
              scanId: originalScanId,
              name: `Scan ${originalScanId}`,
              target: 'Unknown Target',
              status: 'ABORTED',
              createdAt: new Date().toISOString()
            };
            
            // Try to get actual scan details if possible
            try {
              const scanDetails = await scanService.getScanDetails(originalScanId);
              if (scanDetails && scanDetails.scan) {
                Object.assign(scanInfo, {
                  name: scanDetails.scan.name || scanInfo.name,
                  target: scanDetails.scan.target || scanInfo.target,
                  status: scanDetails.scan.status || 'ABORTED',
                  createdAt: scanDetails.scan.created || scanDetails.scan.createdAt || scanInfo.createdAt
                });
              }
            } catch (detailsError) {
              console.warn('Could not get scan details, using default values:', detailsError);
            }
            
            // Prepare event data
            const eventsByType = {};
            
            // Try to load data for each event type
            if (eventTypes && eventTypes.length > 0) {
              for (const type of eventTypes) {
                try {
                  console.log(`Loading events for type ${type}`);
                  const typeResults = await scanService.getScanResultsByType(originalScanId, type, 1, 1000);
                  if (typeResults && typeResults.events && typeResults.events.length > 0) {
                    eventsByType[type] = typeResults.events;
                  }
                } catch (typeError) {
                  console.warn(`Error loading events for type ${type}:`, typeError);
                  eventsByType[type] = [];
                }
              }
            }
            
            // Generate manual report
            const manualReport = {
              reportData: {
                scanInfo,
                eventTypes,
                eventsByType
              }
            };
            
            // For CSV, convert to CSV and trigger download
            if (format === 'csv') {
              // Simple CSV conversion
              let csv = 'Type,Data,Module,Time\n';
              
              Object.entries(eventsByType).forEach(([type, events]) => {
                events.forEach(event => {
                  const time = event.time || new Date().toISOString();
                  const data = event.data ? `"${event.data.replace(/"/g, '""')}"` : '';
                  const module = event.module || 'Unknown';
                  csv += `${type},${data},${module},${time}\n`;
                });
              });
              
              // Create a download link
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              
              const a = document.createElement('a');
              a.href = url;
              a.download = `scan-${scanId}-report.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              
              // Clean up the URL object
              setTimeout(() => {
                URL.revokeObjectURL(url);
              }, 100);
              
              return { success: true, message: 'CSV exported manually successfully' };
            }
            
            return manualReport;
          }
          
          // Handle authentication issues
        if (response.status === 401) {
          console.log('Authentication failed, attempting token refresh...');
          // If we get a 401, try to refresh the token
          try {
            await authService.getCurrentUser();
            // Get the refreshed token
            const newToken = localStorage.getItem('token');
            if (newToken && newToken !== token) {
              console.log('Token refreshed, retrying export...');
              // Retry the export once with the new token
              return scanService.exportScanReport(scanId, format);
            } else {
              console.error('Token refresh did not produce a new token');
              throw new Error('Authentication failed. Please log in again.');
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            throw new Error('Authentication failed. Please log in again.');
          }
        }
          
        throw new Error(`Export failed with status: ${response.status}`);
      }
      
      if (format === 'csv') {
        // For CSV, create a blob and download
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `scan-${scanId}-report.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Clean up the URL object
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
        
        return { success: true, message: 'CSV exported successfully' };
      } else {
        // For JSON format, return the data directly
        const data = await response.json();
        if (!data || !data.reportData) {
          throw new Error('Failed to get report data');
        }
        
        // Log export metadata
        if (data.reportData?.scanInfo) {
          const info = data.reportData.scanInfo;
          console.log(`Export mode: ${info.exportMode || 'standard'}`);
          console.log(`Total events in scan: ${info.totalEventsInScan}`);
          console.log(`Events in export: ${info.eventsInExport}`);
          console.log(`All data included: ${info.allDataIncluded}`);
          
          if (info.streamingUsed) {
            console.log('✅ Streaming mode used - all data loaded in chunks');
          }
        }
        
        return data;
        }
      } catch (fetchError) {
        console.error('Error fetching report data:', fetchError);
        throw fetchError;
      }
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  },
  
  // Get scan status from SpiderFoot
  getScanStatus: async (scanId) => {
    try {
      if (!scanId) {
        console.error('No scanId provided to getScanStatus');
        return 'NOT_FOUND';
      }
      
      const formData = new URLSearchParams();
      formData.append('id', scanId);
      
      const response = await fetch(`${config.SF_PROXY_URL}/scanstatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          'x-auth-token': localStorage.getItem('token')
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Raw scan status for ${scanId}:`, data);
        
        // Handle various response formats
        if (data === null || data === undefined) {
          return 'NOT_FOUND';
        }
        
        if (typeof data === 'string') {
          return data;
        }
        
        if (typeof data === 'object') {
          if (data.status) return data.status;
          if (data.error && data.error.includes('not found')) return 'NOT_FOUND';
        }
        
        return data;
      }
      
      // Handle error status codes
      if (response.status === 404) {
        return 'NOT_FOUND';
      }
      
      console.error(`Error response from scanstatus: ${response.status}`);
      return 'ERROR';
    } catch (error) {
      console.error('Error getting scan status:', error);
      return 'ERROR';
    }
  },

  // Update scan status in our database
  updateScanStatus: async (scanId, status) => {
    try {
      const response = await api.put(`/scans/${scanId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating scan status:', error);
      throw error;
    }
  },

  // Enhanced stopScan method
  stopScan: async (scanId) => {
    try {
      console.log(`Attempting to stop scan ${scanId}`);
      
      // Get original scanId without timestamp suffix for SpiderFoot API
      const originalScanId = scanId.split('_')[0];
      console.log(`Original scanId for SpiderFoot: ${originalScanId}`);
      
      let dbScanExists = true;
      // First check if scan exists in our database
      try {
        await api.get(`/scans/${scanId}`);
      } catch (error) {
        if (error.response?.status === 404) {
          dbScanExists = false;
          console.log(`Scan ${scanId} not found in database`);
        }
      }

      // Try to stop scan through SpiderFoot directly - attempt this first
      let sfStopSuccessful = false;
      try {
        console.log(`Attempting to stop scan ${originalScanId} directly through SpiderFoot`);
        
        // Create form data for proper POST request
        const formData = new URLSearchParams();
        formData.append('id', originalScanId);
        
        const response = await fetch(`${config.SF_PROXY_URL}/stopscan`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json',
            'x-auth-token': localStorage.getItem('token')
          },
          body: formData
        });
        
        sfStopSuccessful = response.ok;
        console.log(`SpiderFoot stop attempt result: ${sfStopSuccessful ? 'success' : 'failed'}`);
      } catch (sfError) {
        console.error('Error stopping scan directly through SpiderFoot:', sfError);
      }

      // If scan exists in database, update its status or try backend stop
      if (dbScanExists) {
        // First try stopping through backend API
        try {
          console.log(`Stopping scan ${scanId} through backend`);
          await api.post(`/scans/${scanId}/stop`);
          console.log(`Successfully sent stop command through backend API`);
        } catch (backendError) {
          console.error('Error stopping scan through backend:', backendError);
          
          // If backend stop failed but SpiderFoot stop succeeded, update DB status
          if (sfStopSuccessful) {
            try {
              console.log(`Updating scan status in database to FINISHED`);
              await api.put(`/scans/${scanId}/status`, { status: 'FINISHED' });
              console.log(`Successfully updated scan status in database`);
            } catch (updateError) {
              console.error('Error updating scan status:', updateError);
            }
          }
        }
      } else {
        console.log(`Scan not in database, no status update needed`);
      }

      // Return appropriate success message based on what happened
      if (sfStopSuccessful) {
        return {
          success: true,
          message: 'Scan stopped successfully in SpiderFoot'
        };
      } else if (!dbScanExists) {
        return {
          success: true,
          notFound: true,
          message: 'Scan not found in database. No action needed.'
        };
      } else {
        // At this point, we tried both approaches but couldn't confirm success
        // Force update the database anyway as a last resort
        try {
          await api.put(`/scans/${scanId}/status`, { status: 'FINISHED' });
          return {
            success: true,
            forceUpdated: true,
            message: 'Forced scan status update to FINISHED in database'
          };
        } catch (finalError) {
          console.error('Final attempt to update status failed:', finalError);
          return {
            success: false,
            message: 'Failed to stop scan through all available methods'
          };
        }
      }
    } catch (error) {
      console.error('Stop scan error:', error);
      return {
        success: false,
        message: error.message || 'Failed to stop scan'
      };
    }
  },

  // Delete a scan by its ID
  deleteScan: async (scanId) => {
    try {
      console.log(`Deleting scan with ID: ${scanId}`);
      const response = await api.delete(`/scans/${scanId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting scan ${scanId}:`, error);
      throw error;
    }
  },
  
  // Force delete a scan by its ID (removes it from everywhere)
  forceDeleteScan: async (scanId) => {
    try {
      console.log(`Force deleting scan with ID: ${scanId}`);
      const response = await api.delete(`/scans/${scanId}?force=true`);
      return response.data;
    } catch (error) {
      console.error(`Error force deleting scan ${scanId}:`, error);
      throw error;
    }
  },
  
  // Delete a duplicate scan by its ID (with optimized handling)
  deleteDuplicateScan: async (scanId) => {
    try {
      console.log(`Deleting duplicate scan with ID: ${scanId}`);
      const response = await api.delete(`/scans/${scanId}?duplicate=true`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting duplicate scan ${scanId}:`, error);
      throw error;
    }
  },
  
  // Get visualization data (SpiderFoot API)
  getScanVisualization: async (scanId) => {
    try {
      const response = await api.get(`/scans/${scanId}/visualization`);
      return response.data;
    } catch (error) {
      console.error('Backend API error (getScanVisualization):', error);
      throw error;
    }
  },
  
  // Get visualization data from MongoDB (complete parent-child relationships)
  getScanVisualizationFromMongoDB: async (scanId) => {
    try {
      console.log(`[getScanVisualizationFromMongoDB] Fetching visualization from MongoDB for scan ${scanId}`);
      const response = await api.get(`/scans/${scanId}/visualization/mongodb`);
      console.log(`[getScanVisualizationFromMongoDB] Success: ${response.data?.nodes?.length || 0} nodes, ${response.data?.edges?.length || 0} edges`);
      return response.data;
    } catch (error) {
      console.error('Backend API error (getScanVisualizationFromMongoDB):', error);
      throw error;
    }
  },
  
  // Get comprehensive risk analysis for a scan
  getScanRiskAnalysis: async (scanId, forceRecalculate = false) => {
    try {
      const params = forceRecalculate ? { forceRecalculate: 'true' } : {};
      console.log(`[getScanRiskAnalysis] Fetching risk analysis for scan ${scanId}, forceRecalculate: ${forceRecalculate}`);
      
      const response = await api.get(`/scans/${scanId}/risk-analysis`, { params });
      
      if (response.data.success) {
        const riskAnalysis = response.data.riskAnalysis;
        
        // Add client-side caching metadata
        if (riskAnalysis) {
          riskAnalysis.clientCacheTimestamp = Date.now();
          riskAnalysis.isFromCache = riskAnalysis.isCached || false;
          riskAnalysis.cacheInfo = riskAnalysis.cacheSource || 'unknown';
          
          // Log cache status for debugging
          if (riskAnalysis.isFromCache) {
            console.log(`[getScanRiskAnalysis] Received cached analysis for scan ${scanId} from ${riskAnalysis.cacheInfo}`);
          } else {
            console.log(`[getScanRiskAnalysis] Received fresh analysis for scan ${scanId}`);
          }
          
          // Store in session storage for additional client-side caching (for completed scans only)
          if (riskAnalysis.isFinalAnalysis && riskAnalysis.scanStatus !== 'RUNNING') {
            try {
              const cacheKey = `riskAnalysis_${scanId}`;
              sessionStorage.setItem(cacheKey, JSON.stringify({
                data: riskAnalysis,
                timestamp: Date.now(),
                isFinal: true
              }));
              console.log(`[getScanRiskAnalysis] Cached final analysis in sessionStorage for scan ${scanId}`);
            } catch (storageError) {
              console.warn(`[getScanRiskAnalysis] Failed to cache in sessionStorage:`, storageError);
            }
          }
        }
        
        console.log(`[getScanRiskAnalysis] Successfully retrieved risk analysis for scan ${scanId}`);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to get risk analysis');
      }
    } catch (error) {
      console.error('Backend API error (getScanRiskAnalysis):', error);
      
      // Try to get from session storage as fallback for completed scans
      if (!forceRecalculate) {
        try {
          const cacheKey = `riskAnalysis_${scanId}`;
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const cachedData = JSON.parse(cached);
            const age = Date.now() - cachedData.timestamp;
            // Use cached data if it's less than 1 hour old and it's a final analysis
            if (age < 3600000 && cachedData.isFinal) {
              console.log(`[getScanRiskAnalysis] Using sessionStorage fallback for scan ${scanId}`);
              cachedData.data.isFromCache = true;
              cachedData.data.cacheInfo = 'SessionStorage';
              return { success: true, riskAnalysis: cachedData.data };
            }
          }
        } catch (cacheError) {
          console.warn(`[getScanRiskAnalysis] Failed to retrieve from sessionStorage:`, cacheError);
        }
      }
      
      throw error;
    }
  },

  // Export risk analysis data as CSV
  exportScanRiskAnalysisCSV: async (scanId) => {
    try {
      console.log(`[exportScanRiskAnalysisCSV] Exporting risk analysis CSV for scan ${scanId}`);
      const response = await api.get(`/scans/${scanId}/risk-analysis/export/csv`, {
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `risk_analysis_${scanId}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`[exportScanRiskAnalysisCSV] Successfully exported risk analysis CSV for scan ${scanId}`);
      return { success: true, message: 'CSV exported successfully' };
    } catch (error) {
      console.error('Backend API error (exportScanRiskAnalysisCSV):', error);
      throw error;
    }
  },

  // Recalculate risk scores for existing scan events (admin only)
  recalculateScanRiskScores: async (scanId) => {
    try {
      console.log(`[recalculateScanRiskScores] Recalculating risk scores for scan ${scanId}`);
      const response = await api.post(`/scans/${scanId}/recalculate-risk`);
      console.log(`[recalculateScanRiskScores] Successfully recalculated risk scores for scan ${scanId}`);
      return response.data;
    } catch (error) {
      console.error('Backend API error (recalculateScanRiskScores):', error);
      throw error;
    }
  },

  // Accept risk for a finding
  acceptFindingRisk: async (scanId, findingId, reason, expiryDate = null, notes = null) => {
    try {
      console.log(`[acceptFindingRisk] Accepting risk for finding ${findingId} in scan ${scanId}`);
      const response = await api.post(`/scans/${scanId}/findings/${findingId}/accept-risk`, {
        reason,
        expiryDate,
        notes
      });
      console.log(`[acceptFindingRisk] Successfully accepted risk for finding ${findingId}`);
      return response.data;
    } catch (error) {
      console.error('Backend API error (acceptFindingRisk):', error);
      throw error;
    }
  },

  // Revoke risk acceptance for a finding
  revokeFindingRiskAcceptance: async (scanId, findingId, notes = null) => {
    try {
      console.log(`[revokeFindingRiskAcceptance] Revoking risk acceptance for finding ${findingId} in scan ${scanId}`);
      const response = await api.post(`/scans/${scanId}/findings/${findingId}/revoke-risk`, {
        notes
      });
      console.log(`[revokeFindingRiskAcceptance] Successfully revoked risk acceptance for finding ${findingId}`);
      return response.data;
    } catch (error) {
      console.error('Backend API error (revokeFindingRiskAcceptance):', error);
      throw error;
    }
  },

  // Get findings with expired risk acceptance
  getExpiredRiskAcceptances: async (scanId) => {
    try {
      console.log(`[getExpiredRiskAcceptances] Getting expired risk acceptances for scan ${scanId}`);
      const response = await api.get(`/scans/${scanId}/expired-risk-acceptances`);
      console.log(`[getExpiredRiskAcceptances] Found ${response.data.count} expired risk acceptances`);
      return response.data;
    } catch (error) {
      console.error('Backend API error (getExpiredRiskAcceptances):', error);
      throw error;
    }
  },
  
  // Get detailed findings from backend or SpiderFoot
  getDetailedFindings: async (findingIds) => {
    try {
      console.log('Getting detailed findings for:', findingIds);
      
      if (!findingIds || !Array.isArray(findingIds) || findingIds.length === 0) {
        return [];
      }
      
      // For now, simulate detailed findings by transforming the IDs into detailed objects
      // In a real implementation, this would make an API call to get the actual finding details
      return findingIds.map(id => ({
        id,
        severity: ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'][Math.floor(Math.random() * 5)],
        title: `Security Finding ${id.substring(0, 8)}`,
        shortDescription: `This is a short description for finding ${id.substring(0, 8)}`,
        fullDescription: `This is a detailed description of the security finding ${id.substring(0, 8)}. It includes comprehensive information about the vulnerability, its implications, and contextual data.`,
        status: ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'FALSE_POSITIVE'][Math.floor(Math.random() * 4)],
        category: ['Network', 'Application', 'Data', 'Configuration', 'Authentication'][Math.floor(Math.random() * 5)],
        impact: 'This finding could potentially impact the security and stability of the system.',
        recommendation: 'It is recommended to address this finding by following security best practices.',
        detectedAt: new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toISOString(),
        codeSnippet: Math.random() > 0.5 ? `function vulnerable() {\n  // This is a vulnerable piece of code\n  const data = req.params.id;\n  return eval(data); // Vulnerable to code injection\n}` : null,
        resourceAffected: `resource-${Math.floor(Math.random() * 1000)}`,
        tags: ['security', 'vulnerability', 'scan-result'].slice(0, Math.floor(Math.random() * 3) + 1),
        references: [
          { title: 'OWASP Top 10', url: 'https://owasp.org/Top10/' },
          { title: 'CWE Database', url: 'https://cwe.mitre.org/' }
        ].slice(0, Math.floor(Math.random() * 2) + 1)
      }));
    } catch (error) {
      console.error('Error getting detailed findings:', error);
      throw error;
    }
  },
  
  // Save processed security findings for a scan to MongoDB
  saveScanFindings: async (scanId, findings) => {
    try {
      console.log(`Saving processed findings for scan ${scanId}`);
      
      if (!findings || !Array.isArray(findings) || findings.length === 0) {
        console.warn('No findings to save');
        return { success: false, message: 'No findings to save' };
      }
      
      // Save processed findings to MongoDB for faster access next time
      const response = await api.post(`/scans/${scanId}/findings`, {
        findings: findings.map(finding => ({
          scanId,
          findingId: finding.id || `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          type: finding.type || 'UNKNOWN',
          data: finding.data || '',
          module: finding.module || '',
          riskLevel: finding.riskLevel || 'INFO',
          timestamp: finding.timestamp || new Date().toISOString(),
          recommendation: finding.recommendation || '',
          status: (finding.status || 'open').toUpperCase()
        }))
      });
      
      console.log(`Successfully saved ${findings.length} processed findings to MongoDB`);
      return response.data;
    } catch (error) {
      console.error('Error saving processed findings:', error);
      
      // Still save to localStorage as fallback
      try {
        localStorage.setItem(`processed_findings_${scanId}`, JSON.stringify(findings));
        console.log('Saved findings to localStorage as fallback');
      } catch (localStorageError) {
        console.error('Error saving to localStorage:', localStorageError);
      }
      
      return { success: false, error: error.message };
    }
  },
  
  // Get processed security findings for a scan from MongoDB
  getScanFindings: async (scanId) => {
    try {
      console.log(`Retrieving processed findings for scan ${scanId}`);
      
      const response = await api.get(`/scans/${scanId}/findings`);
      
      if (response.data && Array.isArray(response.data.findings)) {
        console.log(`Retrieved ${response.data.findings.length} processed findings from MongoDB`);
        return response.data.findings;
      }
      
      return [];
    } catch (error) {
      console.error('Error retrieving processed findings:', error);
      
      // Try to get from localStorage as fallback
      try {
        const cachedFindings = localStorage.getItem(`processed_findings_${scanId}`);
        if (cachedFindings) {
          const findings = JSON.parse(cachedFindings);
          console.log(`Retrieved ${findings.length} findings from localStorage cache`);
          return findings;
        }
      } catch (localStorageError) {
        console.error('Error retrieving from localStorage:', localStorageError);
      }
      
      return [];
    }
  },
  
  // Get raw scan findings (includes all data from SpiderFoot and other sources)
  getScanRawFindings: async (scanId) => {
    try {
      console.log(`Retrieving raw findings for scan ${scanId} directly from backend`);
      
      // Try backend's raw data endpoint first
      const response = await api.get(`/scans/${scanId}/raw-findings`);
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`Retrieved ${response.data.length} raw findings from backend`);
        return response.data;
      }
      
      // If that fails, try SpiderFoot API through the proxy
      const originalScanId = getOriginalScanId(scanId);
      console.log(`Trying SpiderFoot API for scan ${originalScanId}`);
      
      const spiderFootFormData = new URLSearchParams();
      spiderFootFormData.append('id', originalScanId);
      spiderFootFormData.append('eventType', '');
      spiderFootFormData.append('by', 'id');
      
      const sfResponse = await fetch(`${config.SF_PROXY_URL}/scaneventresultsall`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        body: spiderFootFormData
      });
      
      if (sfResponse.ok) {
        const data = await sfResponse.json();
        if (Array.isArray(data)) {
          console.log(`Retrieved ${data.length} raw findings from SpiderFoot`);
          
          // Process the raw data into a consistent format
          const processedData = data.map((item, index) => {
            return {
              id: `sf-${originalScanId}-${index}`,
              findingId: `sf-${originalScanId}-${index}`,
              scanId: scanId,
              type: item[3] || 'UNKNOWN',
              module: item[2] || 'SpiderFoot',
              data: item[1] || '',
              source: item[2] || 'SpiderFoot',
              created: new Date(item[0] * 1000).toISOString(),
              status: 'OPEN',
              riskLevel: determineRiskLevel(item)
            };
          });
          
          return processedData;
        }
      }
      
      // If all else fails, try to see if there's a cached version
      const cachedRawFindings = localStorage.getItem(`raw_findings_${scanId}`);
      if (cachedRawFindings) {
        try {
          const findings = JSON.parse(cachedRawFindings);
          console.log(`Retrieved ${findings.length} raw findings from localStorage cache`);
          return findings;
        } catch (e) {
          console.error('Error parsing cached raw findings:', e);
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error retrieving raw findings:', error);
      return [];
    }
  },

  // --- Vulnerability Management (Managed Findings) ---
  getManagedSummary: async (scanId) => {
    try {
      const response = await api.get(`/vuln/scan/${scanId}/summary`);
      return response.data;
    } catch (e) {
      console.error('Error fetching managed findings summary:', e);
      throw e;
    }
  },
  getManagedFindings: async ({ scanId, page=1, limit=50, status, riskLevel, module, search }) => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);
    if (riskLevel) params.append('riskLevel', riskLevel);
    if (module) params.append('module', module);
    if (search) params.append('search', search);
    try {
      const response = await api.get(`/vuln/scan/${scanId}/findings?${params.toString()}`);
      return response.data;
    } catch (e) { console.error('Error fetching managed findings list:', e); throw e; }
  },
  ensureManagedSnapshot: async (scanId) => {
    try { return (await api.post(`/vuln/scan/${scanId}/snapshot`)).data; } catch (e) { console.error('Snapshot ensure error:', e); throw e; }
  },
  updateManagedStatus: async (findingId, status) => {
    try { return (await api.patch(`/vuln/finding/${findingId}/status`, { status })).data; } catch (e) { console.error('Status update error:', e); throw e; }
  },
  addManagedComment: async (findingId, comment) => {
    try { return (await api.post(`/vuln/finding/${findingId}/comment`, { comment })).data; } catch (e) { console.error('Comment add error:', e); throw e; }
  },
  assignManagedFinding: async (findingId, assignee) => {
    try { return (await api.patch(`/vuln/finding/${findingId}/assign`, { assignee })).data; } catch (e) { console.error('Assign error:', e); throw e; }
  },
  updateManagedSla: async (findingId, hours) => {
    try { return (await api.patch(`/vuln/finding/${findingId}/sla`, { hours })).data; } catch (e) { console.error('SLA update error:', e); throw e; }
  },
  bulkManagedStatus: async (scanId, ids, status) => {
    try { return (await api.post(`/vuln/scan/${scanId}/bulk/status`, { ids, status })).data; } catch (e) { console.error('Bulk status error:', e); throw e; }
  },
  
  // Update finding action function
  updateFindingStatus: async (scanId, findingId, status, actionDetails) => {
    try {
      console.log(`Updating finding ${findingId} status to ${status}`);
      
      // Normalize status to uppercase
      const normalizedStatus = status.toUpperCase();
      
      // Create action record
      const action = {
        notes: actionDetails?.notes || `Status changed to ${normalizedStatus}`,
        user: actionDetails?.user || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).name : 'User')
      };
      
      // Send update to backend
      const response = await api.post(`/scans/${scanId}/findings/${findingId}/status`, {
        status: normalizedStatus,
        action
      });
      
      console.log(`Successfully updated finding status`);
      
      // Also save action to localStorage as backup
      try {
        // Get existing actions
        const existingActionsJSON = localStorage.getItem(`finding_actions_${scanId}`);
        const existingActions = existingActionsJSON ? JSON.parse(existingActionsJSON) : [];
        
        // Add new action to history
        existingActions.push({
          findingId,
          scanId,
          actionType: 'STATUS_CHANGE',
          status: normalizedStatus,
          notes: actionDetails?.notes || `Status changed to ${normalizedStatus}`,
          user: actionDetails?.user || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).name : 'User'),
          timestamp: new Date().toISOString()
        });
        
        // Save updated actions
        localStorage.setItem(`finding_actions_${scanId}`, JSON.stringify(existingActions));
      } catch (localStorageError) {
        console.warn('Error updating localStorage:', localStorageError);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating finding status:', error);
      
      // Add to localStorage even if API fails
      try {
        const action = {
          findingId,
          scanId,
          status,
          actionType: 'STATUS_CHANGE',
          notes: actionDetails?.notes || `Status changed to ${status}`,
          user: actionDetails?.user || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).name : 'User'),
          timestamp: new Date().toISOString(),
          pendingSync: true // Mark as pending sync
        };
        
        // Get existing actions
        const existingActionsJSON = localStorage.getItem(`finding_actions_${scanId}`);
        const existingActions = existingActionsJSON ? JSON.parse(existingActionsJSON) : [];
        
        // Add new action to history
        existingActions.push(action);
        
        // Save updated actions
        localStorage.setItem(`finding_actions_${scanId}`, JSON.stringify(existingActions));
        
        return { success: true, pendingSync: true, action };
      } catch (localStorageError) {
        console.error('Error saving to localStorage:', localStorageError);
      }
      
      return { success: false, error: error.message };
    }
  },
  
  // Update finding severity
  updateFindingSeverity: async (scanId, findingId, severity, actionDetails) => {
    try {
      console.log(`Updating finding ${findingId} severity to ${severity}`);
      
      // Create action record
      const action = {
        findingId,
        scanId,
        previousSeverity: actionDetails?.previousSeverity || 'UNKNOWN',
        notes: actionDetails?.notes || `Severity changed to ${severity}`,
        user: actionDetails?.user || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).name : 'User')
      };
      
      // Send update to backend
      const response = await api.post(`/scans/${scanId}/findings/${findingId}/severity`, {
        severity,
        action
      });
      
      console.log(`Successfully updated finding severity`);
      
      // Also save action to localStorage as backup
      try {
        // Get existing actions
        const existingActionsJSON = localStorage.getItem(`finding_actions_${scanId}`);
        const existingActions = existingActionsJSON ? JSON.parse(existingActionsJSON) : [];
        
        // Add new action to history
        existingActions.push({
          ...action,
          actionType: 'SEVERITY_CHANGE',
          severity,
          timestamp: new Date().toISOString()
        });
        
        // Save updated actions
        localStorage.setItem(`finding_actions_${scanId}`, JSON.stringify(existingActions));
      } catch (localStorageError) {
        console.warn('Error updating localStorage:', localStorageError);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating finding severity:', error);
      
      // Add to localStorage even if API fails
      try {
        const action = {
          findingId,
          scanId,
          severity,
          actionType: 'SEVERITY_CHANGE',
          previousSeverity: actionDetails?.previousSeverity || 'UNKNOWN',
          notes: actionDetails?.notes || `Severity changed to ${severity}`,
          user: actionDetails?.user || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).name : 'User'),
          timestamp: new Date().toISOString(),
          pendingSync: true // Mark as pending sync
        };
        
        // Get existing actions
        const existingActionsJSON = localStorage.getItem(`finding_actions_${scanId}`);
        const existingActions = existingActionsJSON ? JSON.parse(existingActionsJSON) : [];
        
        // Add new action to history
        existingActions.push(action);
        
        // Save updated actions
        localStorage.setItem(`finding_actions_${scanId}`, JSON.stringify(existingActions));
        
        return { success: true, pendingSync: true, action };
      } catch (localStorageError) {
        console.error('Error saving to localStorage:', localStorageError);
      }
      
      return { success: false, error: error.message };
    }
  },
  
  // Add comment to finding
  addFindingComment: async (scanId, findingId, comment) => {
    try {
      console.log(`Adding comment to finding ${findingId}`);
      
      const response = await api.post(`/scans/${scanId}/findings/${findingId}/comment`, {
        comment
      });
      
      console.log(`Successfully added comment to finding`);
      return response.data;
    } catch (error) {
      console.error('Error adding comment to finding:', error);
      
      // Add to localStorage even if API fails
      try {
        // Create action entry for localStorage
        const action = {
          findingId,
          scanId,
          actionType: 'COMMENT',
          notes: comment,
          user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).name : 'User',
          timestamp: new Date().toISOString(),
          pendingSync: true
        };
        
        // Get existing actions - proper structure for finding actions
        const existingActionsJSON = localStorage.getItem(`finding_actions_${scanId}`);
        let existingActions = {};
        
        try {
          existingActions = existingActionsJSON ? JSON.parse(existingActionsJSON) : {};
        } catch (parseError) {
          console.error('Error parsing existing actions:', parseError);
          existingActions = {};
        }
        
        // Add or update the action for this finding
        existingActions[findingId] = {
          ...(existingActions[findingId] || {}),
          lastAction: 'COMMENT',
          lastActionTime: new Date().toISOString()
        };
        
        // Get action history
        const historyJSON = localStorage.getItem(`action_history_${scanId}`);
        let history = [];
        
        try {
          history = historyJSON ? JSON.parse(historyJSON) : [];
        } catch (parseError) {
          console.error('Error parsing action history:', parseError);
          history = [];
        }
        
        // Add to action history
        history.push(action);
        
        // Save updated history
        localStorage.setItem(`action_history_${scanId}`, JSON.stringify(history));
        
        // Save finding actions
        localStorage.setItem(`finding_actions_${scanId}`, JSON.stringify(existingActions));
        
        console.log('Saved comment to localStorage for offline sync');
        return { success: true, pendingSync: true, action };
      } catch (localStorageError) {
        console.error('Error saving to localStorage:', localStorageError);
      }
      
      return { success: false, error: error.message };
    }
  },
  
  // Get finding action history
  getFindingActionHistory: async (scanId, findingId) => {
    try {
      console.log(`Getting action history for finding ${findingId}`);
      
      const response = await api.get(`/scans/${scanId}/findings/${findingId}/history`);
      
      console.log(`Successfully retrieved action history`);
      
      // If response data is empty array, try to get from localStorage
      if (!response.data.actions || response.data.actions.length === 0) {
        console.log('No action history returned from API, checking localStorage');
        return getFindingHistoryFromLocalStorage(scanId, findingId);
      }
      
      return response.data.actions || [];
    } catch (error) {
      console.error('Error getting finding action history:', error);
      
      // Try to get from localStorage as fallback
      return getFindingHistoryFromLocalStorage(scanId, findingId);
    }
  },
  
  // Assign finding to user
  assignFinding: async (scanId, findingId, assignedTo) => {
    try {
      console.log(`Assigning finding ${findingId} to ${assignedTo}`);
      
      const response = await api.post(`/scans/${scanId}/findings/${findingId}/assign`, {
        assignedTo
      });
      
      console.log(`Successfully assigned finding`);
      return response.data;
    } catch (error) {
      console.error('Error assigning finding:', error);
      
      // Add to localStorage even if API fails
      try {
        const action = {
          findingId,
          scanId,
          actionType: 'ASSIGNMENT',
          notes: `Assigned to ${assignedTo}`,
          user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).name : 'User',
          timestamp: new Date().toISOString(),
          pendingSync: true
        };
        
        // Get existing actions
        const existingActionsJSON = localStorage.getItem(`finding_actions_${scanId}`);
        const existingActions = existingActionsJSON ? JSON.parse(existingActionsJSON) : [];
        
        // Add new action to history
        existingActions.push(action);
        
        // Save updated actions
        localStorage.setItem(`finding_actions_${scanId}`, JSON.stringify(existingActions));
        
        return { success: true, pendingSync: true, action };
      } catch (localStorageError) {
        console.error('Error saving to localStorage:', localStorageError);
      }
      
      return { success: false, error: error.message };
    }
  },
  
  // Sync pending actions from localStorage to MongoDB
  syncPendingActions: async (scanId) => {
    try {
      // Get all actions from localStorage for this scan
      const actionsJSON = localStorage.getItem(`finding_actions_${scanId}`);
      if (!actionsJSON) {
        console.log('No actions to sync for scan', scanId);
        return { success: true, synced: 0 };
      }
      
      const allActions = JSON.parse(actionsJSON);
      
      // Ensure allActions is an array
      if (!Array.isArray(allActions)) {
        console.log('Invalid actions data structure for scan', scanId);
        return { success: true, synced: 0 };
      }
      
      // Filter actions marked as pending sync
      const pendingActions = allActions.filter(action => action.pendingSync === true);
      
      if (pendingActions.length === 0) {
        console.log('No pending actions to sync for scan', scanId);
        return { success: true, synced: 0 };
      }
      
      console.log(`Syncing ${pendingActions.length} pending actions for scan ${scanId}`);
      
      // Send pending actions to backend for syncing
      const response = await api.post(`/scans/${scanId}/findings/sync-pending-actions`, {
        pendingActions
      });
      
      if (response.data.success) {
        // Remove synced actions from pending status
        const successfulIds = response.data.results.successful.map(result => `${result.findingId}-${result.actionType}`);
        
        const updatedActions = allActions.map(action => {
          const actionId = `${action.findingId}-${action.actionType}`;
          if (action.pendingSync && successfulIds.includes(actionId)) {
            return { ...action, pendingSync: false };
          }
          return action;
        });
        
        // Save updated actions without pending flag
        localStorage.setItem(`finding_actions_${scanId}`, JSON.stringify(updatedActions));
        
        console.log(`Successfully synced ${response.data.results.successful.length} actions`);
        return response.data;
      }
      
      return { success: false, error: 'Sync failed' };
    } catch (error) {
      console.error('Error syncing pending actions:', error);
      return { success: false, error: error.message };
    }
  },
  
  // Get security analysis insights
  getSecurityInsights: async (scanId) => {
    // Implementation of getSecurityInsights method
  },
  
  // Get all event types for a scan, with comprehensive fallbacks
  getScanEventTypes: async (scanId) => {
    if (!scanId) {
      console.error('No scanId provided to getScanEventTypes');
      return [];
    }
    
    try {
      console.log(`Fetching event types for scan ${scanId}`);
      
      // First try our backend
      const response = await api.get(`/scans/${scanId}/eventtypes`);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        console.log(`Got ${response.data.length} event types from backend`);
        return response.data;
      }
      
      // If backend returned no results, try the scan summary to get types
      console.log('No event types from backend, trying scan summary');
      try {
        const summary = await scanService.getScanSummary(scanId);
        if (summary && summary.byType) {
          const typeKeys = Object.keys(summary.byType);
          console.log(`Got ${typeKeys.length} event types from scan summary`);
          return typeKeys;
        }
      } catch (summaryError) {
        console.error('Error getting event types from summary:', summaryError);
      }
      
      // If that fails, try to get all results and extract types
      console.log('No event types from summary, trying to get all results');
      try {
        const data = await scanService.getScanResultsByType(scanId, 'all', 1, 100);
        if (data && data.events && data.events.length > 0) {
          // Extract unique types
          const types = [...new Set(data.events.map(event => event.type))].filter(Boolean);
          console.log(`Got ${types.length} event types from results`);
          return types;
        }
      } catch (resultsError) {
        console.error('Error getting event types from results:', resultsError);
      }
      
      // If all else fails, try direct SpiderFoot API
      console.log('All previous methods failed, trying direct SpiderFoot API');
      try {
        // Direct SpiderFoot API for scan type summary
        const formData = new URLSearchParams();
        formData.append('id', scanId);
        
        const response = await fetch(`${config.SF_PROXY_URL}/scantypesummary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          },
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data && typeof data === 'object') {
            const types = Object.keys(data);
            console.log(`Got ${types.length} event types from SpiderFoot API`);
            return types;
          }
        }
      } catch (sfError) {
        console.error('SpiderFoot API error:', sfError);
      }
      
      // If nothing else works, return a default comprehensive list of common types
      console.log('All methods failed, returning default comprehensive list');
      return [
        "SUBDOMAIN", "AFFILIATE_DOMAIN", "CO_HOSTED_SITE", "NETBLOCK_OWNER", 
        "IP_ADDRESS", "INTERNET_NAME", "AFFILIATE_IPADDR", "DOMAIN_NAME", 
        "EMAILADDR", "AFFILIATE_INTERNET_NAME", "AFFILIATE_EMAILADDR",
        "LINKED_URL_INTERNAL", "LINKED_URL_EXTERNAL", "SSL_CERTIFICATE_ISSUED", 
        "SSL_CERTIFICATE_ISSUER", "SSL_CERTIFICATE_RAW", "RAW_DNS_RECORDS",
        "WEBSERVER_HTTPHEADERS", "WEBSERVER_TECHNOLOGY", "COUNTRY_NAME",
        "HUMAN_NAME", "PHONE_NUMBER", "BGP_AS_OWNER", "VULNERABILITY",
        "HTTP_CODE", "WEB_CONTENT", "DOMAIN_WHOIS", "LEAKSITE_URL",
        "DARKNET_MENTION_URL", "VULNERABILITY_CVE_CRITICAL", "VULNERABILITY_CVE_HIGH",
        "VULNERABILITY_CVE_MEDIUM", "VULNERABILITY_CVE_LOW", "DOMAIN_REGISTRAR",
        "PUBLIC_CODE_REPO", "MALICIOUS_IPADDR", "DEFACED_INTERNET_NAME",
        "RAW_RIR_DATA", "WEBSERVER_BANNER", "TCP_PORT_OPEN", "URL_WEBFRAMEWORK",
        "SSL_CERTIFICATE_MISMATCH", "DNS_TEXT", "DNS_MX", "DNS_NS"
      ];
    } catch (error) {
      console.error('Error in getScanEventTypes:', error);
      return [];
    }
  },
  
  // Get raw findings directly from database as a fallback
  getScanEventsFromDatabase: async (scanId) => {
    try {
      console.log(`Getting scan events from database for scan ${scanId}`);
      
      const response = await api.get(`/scans/${scanId}/events/database`);
      console.log(`Retrieved ${response.data.length} events from database for scan ${scanId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting scan events from database:', error);
      
      // Try alternative endpoint
      try {
        console.log('Trying alternative endpoint for database events');
        const alternativeResponse = await api.get(`/scans/${scanId}/events`);
        console.log(`Retrieved ${alternativeResponse.data.length} events from alternative endpoint`);
        return alternativeResponse.data;
      } catch (altError) {
        console.error('Error with alternative endpoint:', altError);
      }

      return [];
    }
  },

  // Notification settings methods
  getNotificationSettings: async () => {
    try {
      const response = await api.get('/admin/notifications');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      return handleApiError(error, { success: false, message: 'Failed to load notification settings' });
    }
  },

  updateNotificationSettings: async (settings) => {
    try {
      const response = await api.put('/admin/notifications', settings);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      return handleApiError(error, { success: false, message: 'Failed to update notification settings' });
    }
  },

  testNotificationSettings: async () => {
    try {
      const response = await api.post('/admin/notifications/test');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error testing notification settings:', error);
      return handleApiError(error, { success: false, message: 'Failed to test notification settings' });
    }
  },

  // User notification methods
  getUserNotificationPreferences: async () => {
    try {
      const response = await api.get('/user/notifications/preferences');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching user notification preferences:', error);
      return handleApiError(error, { success: false, message: 'Failed to load notification preferences' });
    }
  },

  updateUserNotificationPreferences: async (preferences) => {
    try {
      const response = await api.put('/user/notifications/preferences', preferences);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating user notification preferences:', error);
      return handleApiError(error, { success: false, message: 'Failed to update notification preferences' });
    }
  },

  addUserEmailAddress: async (email, label) => {
    try {
      const response = await api.post('/user/notifications/email', { email, label });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error adding email address:', error);
      return handleApiError(error, { success: false, message: 'Failed to add email address' });
    }
  },

  removeUserEmailAddress: async (email) => {
    try {
      const response = await api.delete('/user/notifications/email', { data: { email } });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error removing email address:', error);
      return handleApiError(error, { success: false, message: 'Failed to remove email address' });
    }
  },

  setUserPrimaryEmail: async (email) => {
    try {
      const response = await api.post('/user/notifications/email/primary', { email });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error setting primary email:', error);
      return handleApiError(error, { success: false, message: 'Failed to set primary email' });
    }
  },

  toggleUserEmailActive: async (email, isActive) => {
    try {
      const response = await api.post('/user/notifications/email/active', { email, isActive });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error toggling email active:', error);
      return handleApiError(error, { success: false, message: 'Failed to update email status' });
    }
  },

  testUserNotification: async (feature) => {
    try {
      const response = await api.post('/user/notifications/test', { feature });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error testing user notification:', error);
      return handleApiError(error, { success: false, message: 'Failed to test notification' });
    }
  }
};

// Add helper function for getting history from localStorage
const getFindingHistoryFromLocalStorage = (scanId, findingId) => {
  try {
    // First try to get from action_history
    const historyJSON = localStorage.getItem(`action_history_${scanId}`);
    if (historyJSON) {
      try {
        const allActions = JSON.parse(historyJSON);
        const findingActions = allActions.filter(action => 
          (action.findingId === findingId) || 
          (action.finding && (action.finding.id === findingId))
        );
        
        if (findingActions.length > 0) {
          console.log(`Retrieved ${findingActions.length} actions from action_history`);
          return findingActions;
        }
      } catch (historyParseError) {
        console.error('Error parsing action history:', historyParseError);
      }
    }
    
    // Then try finding_actions as fallback
    const actionsJSON = localStorage.getItem(`finding_actions_${scanId}`);
    if (actionsJSON) {
      try {
        const allActions = JSON.parse(actionsJSON);
        
        // Check if it's a map/object structure
        if (allActions && typeof allActions === 'object' && !Array.isArray(allActions)) {
          const findingAction = allActions[findingId];
          if (findingAction) {
            // Convert to array format expected by the component
            return [{
              id: `local-${Date.now()}`,
              findingId,
              actionType: findingAction.lastAction || 'ACTION',
              timestamp: findingAction.lastActionTime || new Date().toISOString(),
              status: findingAction.status,
              severity: findingAction.severity,
              notes: findingAction.notes || 'Action recorded locally',
              user: 'Local User'
            }];
          }
        } 
        // Handle array structure
        else if (Array.isArray(allActions)) {
          const findingActions = allActions.filter(action => 
            action.findingId === findingId
          );
          
          console.log(`Retrieved ${findingActions.length} actions from localStorage`);
          return findingActions;
        }
      } catch (actionsParseError) {
        console.error('Error parsing finding actions:', actionsParseError);
      }
    }
  } catch (localStorageError) {
    console.error('Error retrieving from localStorage:', localStorageError);
  }
  
  return [];
}

// Get all storage keys for pending actions
const getAllPendingActionKeys = () => {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('finding_actions_')) {
      keys.push(key);
    }
  }
  return keys;
};

// Sync all pending actions for a user
export const syncAllPendingActions = async () => {
  try {
    console.log('Syncing all pending actions...');
    const actionKeys = getAllPendingActionKeys();
    
    if (actionKeys.length === 0) {
      console.log('No pending actions to sync');
      return { success: true, message: 'No pending actions found' };
    }
    
    const results = {
      totalScans: actionKeys.length,
      successfulScans: 0,
      failedScans: 0,
      totalSynced: 0
    };
    
    // Process each scan's actions
    for (const key of actionKeys) {
      try {
        // Extract scanId from the key (finding_actions_SCANID)
        const scanId = key.replace('finding_actions_', '');
        
        // Sync actions for this scan
        const syncResult = await scanService.syncPendingActions(scanId);
        
        if (syncResult.success) {
          results.successfulScans++;
          results.totalSynced += syncResult.results?.successful?.length || 0;
        } else {
          results.failedScans++;
        }
      } catch (error) {
        console.error(`Error syncing actions for key ${key}:`, error);
        results.failedScans++;
      }
    }
    
    console.log('All pending actions sync complete:', results);
    return { success: true, results };
  } catch (error) {
    console.error('Error in syncAllPendingActions:', error);
    return { success: false, error: error.message };
  }
};

// Add ticket services
export const ticketService = {
  // Get user's tickets
  getUserTickets: async () => {
    try {
      const response = await api.get('/tickets/user');
      return response.data;
    } catch (error) {
      return handleApiError(error, [], false);
    }
  },
  
  // Get all tickets (admin only)
  getAllTickets: async () => {
    try {
      const response = await api.get('/tickets/all');
      return response.data;
    } catch (error) {
      return handleApiError(error, [], false);
    }
  },
  
  // Get ticket by ID
  getTicketById: async (ticketId) => {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, null, false);
    }
  },
  
  // Create a new ticket
  createTicket: async (ticketData) => {
    try {
      const response = await api.post('/tickets', ticketData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Update ticket status (admin only)
  updateTicketStatus: async (ticketId, status, note) => {
    try {
      const response = await api.put(`/tickets/${ticketId}/status`, { status, note });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Add admin response to ticket (admin only)
  addTicketResponse: async (ticketId, response, status) => {
    try {
      const data = await api.post(`/tickets/${ticketId}/response`, { response, status });
      return data.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Add user reply to ticket
  addTicketReply: async (ticketId, reply) => {
    try {
      const response = await api.post(`/tickets/${ticketId}/reply`, { reply });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Delete ticket (admin only)
  deleteTicket: async (ticketId) => {
    try {
      const response = await api.delete(`/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Breach Monitoring Services
export const breachService = {
  // Get breach monitor settings
  getMonitorSettings: async () => {
    try {
      const response = await api.get('/breach');
      return response.data;
    } catch (error) {
      console.error('Error fetching breach monitor settings:', error);
      return {
        keywords: [],
        emails: [],
        frequency: 'daily',
        isActive: true,
        breaches: []
      };
    }
  },
  
  // Update breach monitor settings
  updateMonitorSettings: async (settings) => {
    try {
      const response = await api.post('/breach', settings);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Delete breach monitor
  deleteMonitor: async () => {
    try {
      const response = await api.delete('/breach');
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Get breach history
  getBreachHistory: async () => {
    try {
      const response = await api.get('/breach/history');
      return response.data;
    } catch (error) {
      console.error('Error fetching breach history:', error);
      return { breaches: [] };
    }
  },
  
  // Clear breach history
  clearBreachHistory: async () => {
    try {
      const response = await api.delete('/breach/history');
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Search Telegram channel for keyword
  searchTelegramChannel: async (keyword) => {
    try {
      const response = await api.post('/breach/search', { keyword });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Trigger immediate breach check
  triggerImmediateCheck: async () => {
    try {
      const response = await api.post('/breach/check');
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Admin Telegram Data Import Services
export const telegramDataService = {
  // Get Telegram data import statistics
  getDataStats: async () => {
    try {
      const response = await api.get('/telegram-data/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching Telegram data stats:', error);
      return {
        totalDocuments: 0,
        dateRange: {
          earliest: null,
          latest: null
        },
        fileStats: []
      };
    }
  },
  
  // Get system information including OS and default paths
  getSystemInfo: async () => {
    try {
      const response = await api.get('/telegram-data/system-info');
      return response.data;
    } catch (error) {
      console.error('Error fetching system info:', error);
      return {
        platform: 'Unknown',
        release: '',
        defaultDataPath: ''
      };
    }
  },
  
  // Import Telegram data from a directory
  importData: async (directory) => {
    try {
      const response = await api.post('/telegram-data/import', { directory });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Check database connection status
  checkDatabaseConnection: async () => {
    try {
      const response = await api.get('/telegram-data/check-db');
      return response.data;
    } catch (error) {
      console.error('Error checking database connection:', error);
      return { 
        connected: false, 
        dbName: null, 
        error: error.message 
      };
    }
  },
  
  // Create a test record to verify data insertion
  createTestRecord: async () => {
    try {
      const response = await api.post('/telegram-data/test-record');
      return response.data;
    } catch (error) {
      console.error('Error creating test record:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || error.message 
      };
    }
  },
  
  // Search imported Telegram data
  searchData: async (query, page = 1, limit = 10) => {
    try {
      const response = await api.post('/telegram-data/search', { query, page, limit });
      return response.data;
    } catch (error) {
      console.error('Error searching Telegram data:', error);
      return {
        success: false,
        results: [],
        pagination: {
          total: 0,
          page: 1,
          limit: 10,
          pages: 0
        }
      };
    }
  },
  
  // Delete specific Telegram data document
  deleteData: async (id) => {
    try {
      const response = await api.delete(`/telegram-data/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Delete all imported Telegram data
  deleteAllData: async () => {
    try {
      const response = await api.delete('/telegram-data', {
        data: { confirm: 'DELETE_ALL' }
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Upload HTML, JSON, or ZIP file for import
  uploadFile: async (file, useDefaultDir = true) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('useDefaultDir', useDefaultDir.toString());
      
      const response = await api.post('/telegram-data/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Fix SQLite tracking count discrepancy
  fixTrackingCount: async () => {
    try {
      const response = await api.post('/api/sqlite-import/fix-tracking');
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }
};

// SQLite Import Services for Telegram Data
export const sqliteImportService = {
  // Get SQLite import configuration
  getConfig: async () => {
    try {
      const response = await api.get('/sqlite-import/config');
      return response.data;
    } catch (error) {
      console.error('Error fetching SQLite import config:', error);
      return {
        dbPath: '',
        isActive: false,
        autoSync: false,
        syncFrequency: 'daily',
        lastSynced: null
      };
    }
  },
  
  // Update SQLite import configuration
  updateConfig: async (config) => {
    try {
      const response = await api.put('/sqlite-import/config', config);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // Test SQLite database connection
  testConnection: async (dbPath) => {
    try {
      const response = await api.post('/sqlite-import/test-connection', { dbPath });
      return response.data;
    } catch (error) {
      console.error('Error testing SQLite connection:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || error.message 
      };
    }
  },
  
  // Run a full sync manually
  runSync: async () => {
    try {
      const response = await api.post('/sqlite-import/sync');
      return response.data;
    } catch (error) {
      console.error('Error running SQLite sync:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || error.message 
      };
    }
  }
};

// AI Chat Service
export const aiService = {
  // Send chat query
  chat: async (query, sessionId = null) => {
    try {
      const response = await api.post('/ai/chat', {
        query,
        sessionId
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Get chat history
  getHistory: async (userId, sessionId = null, limit = 50) => {
    try {
      const params = new URLSearchParams();
      if (sessionId) params.append('sessionId', sessionId);
      if (limit) params.append('limit', limit.toString());
      
      const response = await api.get(`/ai/history/${userId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Trigger reindex (Admin only)
  reindex: async (collectionName = null, force = false) => {
    try {
      const response = await api.post('/ai/reindex', {
        collectionName,
        force
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Get job status (Admin only)
  getJobStatus: async (jobId) => {
    try {
      const response = await api.get(`/ai/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  }
};

export default api;

// Managed findings export helper
export async function exportManagedFindingsCsv({ scanId, filters = {} }) {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.riskLevel) params.append('riskLevel', filters.riskLevel);
  if (filters.module) params.append('module', filters.module);
  if (filters.category) params.append('category', filters.category);
  if (filters.categories && filters.categories.length) params.append('categories', filters.categories.join(','));
  if (filters.search) params.append('search', filters.search);
  const url = `/vuln/scan/${encodeURIComponent(scanId)}/export?` + params.toString();
  const res = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  const date = new Date().toISOString().replace(/[:T]/g,'-').split('.')[0];
  a.href = URL.createObjectURL(blob);
  a.download = `managed-findings-${scanId}-${date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}
